// Shared display formatters. Pure functions, no DOM. Used by views + charts.

/**
 * Escape a string for safe insertion into HTML — both attribute values and
 * text content. The 5-char replacement set covers all HTML-significant chars
 * so the same helper works for both contexts.
 *
 * Use this on every Salesforce-sourced field (BU names, account names, stage
 * strings, reasons, etc.) before interpolating into a template literal that
 * gets assigned to innerHTML. Failing to escape a field that contains a
 * quote or `<` would let attribute boundaries break (XSS) — caught by
 * PR-Agent on PR #13.
 *
 * Returns "" for null/undefined.
 *
 * **NOT safe for `href` / `src` / `style` / `javascript:` attribute
 * contexts.** This helper covers HTML-text and quoted-attribute escaping
 * only. Backtick (`` ` ``) and U+2028 / U+2029 line separators are
 * intentionally NOT escaped — they're not HTML-significant outside JS
 * URI / `<script>` / `style` blocks. If you need to interpolate into
 * one of those contexts, layer a URL-encode (`encodeURIComponent`) or
 * a CSS-escape on top. Sec #1 on PR #54.
 */
export function esc(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

/**
 * Standard money formatter used across all Bounteous data viz.
 * < $1K → "$N", < $1M → "$NK" (no decimals), < $1B → "$N.NM" (1 decimal), else "$N.NB".
 *
 * Negative values render as "-$50K" (sign before $), matching Bounteous brand
 * convention. Boundary clamping: values that would round to "$1000K" promote to
 * "$1.0M" instead — the K bucket caps at 999,499.
 */
export function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  // Promote to M one half-step before the K rounding would emit "1000".
  if (abs >= 999_500) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/** SGNL-214 code-review LOW — centralised constant for day-boundary
 *  math. Hard-coded `86_400_000` previously appeared in 4 call sites
 *  across aggregations.ts + admin_users.ts; this consolidates so a
 *  future leap-second or timezone-aware variant lands in one place. */
export const MS_PER_DAY = 86_400_000;
